import { useMemo } from "react";

import type { CSSObjectWithLabel, StylesConfig } from "react-select";

import { IOption } from "../interfaces";

export const useSelectStyles = (
  isMulti: boolean,
  isInvalid: boolean = false,
): StylesConfig<IOption, boolean> =>
  useMemo(
    () => ({
      container: (base: CSSObjectWithLabel) => ({
        ...base,
        width: "100%",
      }),
      control: (base: CSSObjectWithLabel, { isFocused, menuIsOpen }) => ({
        ...base,
        borderColor: isInvalid
          ? "var(--color-borderAlert)"
          : isFocused && menuIsOpen
            ? "var(--color-borderBrand)"
            : "var(--color-borderSecondary)",
        outline: "none",
        cursor: "pointer",
        borderRadius: "var(--radius-sm)",
        minHeight: "2.5rem",
        boxShadow: "none",
        gap: "0.5rem",
        borderWidth: "2px",
        borderStyle: "solid",
        backgroundColor: "var(--color-backgroundTertiary)",
        ":hover": {
          borderColor: isInvalid
            ? "var(--color-borderAlert)"
            : "var(--color-borderPrimary)",
        },
        ":focus-within": {
          borderColor: isInvalid
            ? "var(--color-borderAlert)"
            : "var(--color-borderBrand)",
          boxShadow: "none",
        },
        transition: "border-color 0.2s ease",
      }),
      valueContainer: (base: CSSObjectWithLabel) => ({
        ...base,
        gap: "0.5rem",
      }),
      multiValue: (base: CSSObjectWithLabel) => ({
        ...base,
        backgroundColor: "var(--color-backgroundBrand)",
        color: "var(--N0)",
        borderRadius: "0.375rem",
        display: "flex",
        alignItems: "center",
        padding: "0.125rem 0.3125rem",
        height: "1.5rem",
      }),
      input: (base: CSSObjectWithLabel) => ({
        ...base,
        margin: 0,
        padding: 0,
        width: "auto",
        fontSize: "inherit",
        color: "var(--color-contentPrimary)",
      }),
      multiValueLabel: (base: CSSObjectWithLabel) => ({
        ...base,
        color: "var(--N0)",
        padding: 0,
        fontSize: "0.938rem",
        fontFamily: "var(--font-family-sans)",
        marginRight: "0.5rem",
      }),
      multiValueRemove: (base: CSSObjectWithLabel) => ({
        ...base,
        color: "var(--N0)",
        ":hover": {
          backgroundColor: "transparent",
        },
      }),
      clearIndicator: (base: CSSObjectWithLabel) => ({
        ...base,
        ...(!isMulti && { display: "none" }),
      }),
      loadingIndicator: (base: CSSObjectWithLabel) => ({
        ...base,
        ...(!isMulti && { display: "none" }),
      }),
      indicatorSeparator: (base: CSSObjectWithLabel) => ({
        ...base,
        ...(!isMulti && { display: "none" }),
      }),

      placeholder: (base: CSSObjectWithLabel) => ({
        ...base,
        color: isInvalid
          ? "var(--color-contentAlert)"
          : "var(--color-contentTertiary)",
        fontSize: "0.938rem",
      }),
      singleValue: (base: CSSObjectWithLabel) => ({
        ...base,
        fontSize: "0.938rem",
        fontFamily: "var(--font-family-sans)",
        color: "var(--color-contentPrimary)",
      }),
      dropdownIndicator: (base: CSSObjectWithLabel) => ({
        ...base,
        padding: "0 0.5rem 0 0",
        color: "var(--color-contentTertiary)",
      }),
      menu: (base: CSSObjectWithLabel) => ({
        ...base,
        border: `1px solid var(--color-borderTertiary)`,
        backgroundColor: "var(--color-backgroundTertiary)",
        boxShadow: "var(--shadow-lg)",
        borderRadius: "var(--radius-lg)",
        zIndex: 9999,
        position: "absolute",
      }),
      menuPortal: (base: CSSObjectWithLabel) => ({
        ...base,
        zIndex: 9999,
      }),
      noOptionsMessage: (base: CSSObjectWithLabel) => ({
        ...base,
        color: "var(--color-contentTertiary)",
        fontSize: "0.938rem",
      }),
      menuList: (base: CSSObjectWithLabel) => ({
        ...base,
        padding: "0.375rem",
        maxHeight: "200px",
        overflowX: "hidden",
      }),
      option: (base: CSSObjectWithLabel, { isSelected, isFocused }) => ({
        ...base,
        display: "flex",
        cursor: "pointer",
        alignItems: "center",
        height: "2.5rem",
        padding: "0.5rem 0.625rem",
        gap: "0.625rem",
        borderRadius: "var(--radius-md)",
        backgroundColor: isSelected
          ? "var(--color-backgroundInformation)"
          : isFocused
            ? "var(--color-backgroundSecondary)"
            : "transparent",
        color: isSelected
          ? "var(--color-contentInformation)"
          : "var(--color-contentPrimary)",
        ":hover": {
          backgroundColor: isSelected
            ? "var(--color-backgroundInformation)"
            : "var(--color-backgroundSecondary)",
        },
      }),
    }),
    [isMulti, isInvalid],
  );
